import { FileText, BookOpen, Repeat, ShieldCheck, Network, Landmark } from "lucide-react";

const CATEGORY = "Finanzoperationen";

export const OPERATIONS = [
  {
    slug: "rechnungen",
    name: "Rechnungen",
    category: CATEGORY,
    icon: FileText,
    tagline: "Rechnungen erstellen, versenden, einziehen.",
    overline: "Rechnungen",
    hero: {
      title: "Rechnungen, die sich selbst begleichen.",
      subtitle:
        "Erstellen Sie konforme Rechnungen per API, versenden Sie sie mit Bezahllink und ziehen Sie den Betrag automatisch ein – vollständig im Ledger abgebildet.",
    },
    stats: [
      { value: "PDF", label: "konform" },
      { value: "Pay-Link", label: "integriert" },
      { value: "Auto", label: "Mahnwesen" },
    ],
    features: [
      { icon: FileText, title: "Konforme Belege", desc: "Rechtssichere PDF-Rechnungen mit Steuerausweis und Nummernkreis." },
      { icon: Network, title: "Integrierter Bezahllink", desc: "Jede Rechnung enthält einen Bezahllink mit Auto-Reconciliation." },
      { icon: Repeat, title: "Mahnwesen", desc: "Automatische Erinnerungen und Eskalationsstufen bei Überfälligkeit." },
      { icon: Landmark, title: "Ledger-Verknüpfung", desc: "Zahlungseingang wird automatisch der Rechnung zugeordnet und verbucht." },
    ],
    architecture: {
      caption: "Rechnungslebenszyklus von Erstellung bis Zahlung",
      layers: [
        { label: "Erstellung", nodes: [{ label: "API", sub: "Positionen" }, { label: "Steuer", sub: "Ausweis" }, { label: "PDF", sub: "Beleg" }] },
        { label: "Versand & Einzug", nodes: [{ label: "Pay-Link", sub: "eingebettet" }, { label: "Mahnung", sub: "Stufen" }] },
        { label: "Verbuchung", nodes: [{ label: "Matching", sub: "auto" }, { label: "Ledger", sub: "Offen/Bezahlt" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/invoices",
      code: [
        "// Rechnung mit Bezahllink erstellen",
        "const invoice = await cashfux.invoices.create({",
        '  customer: "cus_31a",',
        "  items: [",
        '    { name: "Lizenz Pro", qty: 1, unit: 29900 },',
        "  ],",
        '  tax_rate: 19,',
        "});",
        "invoice.hosted_url // Rechnung + Bezahllink",
      ],
      highlights: [
        "Automatischer Steuerausweis",
        "Bezahllink je Rechnung eingebettet",
        "Webhook invoice.paid bei Eingang",
      ],
    },
    useCases: [
      { title: "B2B-Fakturierung", desc: "Rechnungen mit Zahlungsziel und automatischem Einzug." },
      { title: "Freelancer & Agenturen", desc: "Positionen erfassen und mit Bezahllink versenden." },
      { title: "Abo-Rechnungen", desc: "Zyklische Belege aus Subscriptions erzeugen." },
    ],
    faq: [
      { q: "Sind die Rechnungen rechtskonform?", a: "Ja, mit Pflichtangaben, Steuerausweis und fortlaufendem Nummernkreis." },
      { q: "Wie werden Zahlungen zugeordnet?", a: "Über den eingebetteten Bezahllink oder virtuelle IBANs erfolgt die Zuordnung automatisch." },
      { q: "Gibt es ein Mahnwesen?", a: "Ja, mit automatischen Erinnerungen und konfigurierbaren Eskalationsstufen." },
      { q: "Kann ich Rechnungen aus Abos erzeugen?", a: "Ja, Subscriptions erstellen je Zyklus automatisch eine Rechnung." },
    ],
    related: ["subscriptions", "payment-links", "buchhaltung"],
  },
  {
    slug: "buchhaltung",
    name: "Buchhaltung",
    category: CATEGORY,
    icon: BookOpen,
    tagline: "Ledger, Reports und Export für die Buchhaltung.",
    overline: "Buchhaltung",
    hero: {
      title: "Ein Ledger als einzige Wahrheit.",
      subtitle:
        "Jede Bewegung ist doppelt verbucht, unveränderlich und abstimmbar. Exportieren Sie sauber nach DATEV, in Ihr ERP oder als API-Feed.",
    },
    stats: [
      { value: "Double-Entry", label: "immutable" },
      { value: "DATEV", label: "Export" },
      { value: "Echtzeit", label: "Reports" },
    ],
    features: [
      { icon: BookOpen, title: "Double-Entry-Ledger", desc: "Jede Transaktion ist als Soll/Haben unveränderlich festgeschrieben." },
      { icon: FileText, title: "Reports", desc: "Salden, Umsätze und Bewegungen in Echtzeit als Bericht." },
      { icon: Network, title: "Export & ERP", desc: "DATEV-, CSV- und API-Export zur Anbindung Ihrer Buchhaltung." },
      { icon: ShieldCheck, title: "Reconciliation", desc: "Automatischer Abgleich zwischen Ledger, Bank und Belegen." },
    ],
    architecture: {
      caption: "Ledger als Quelle für Reports und Exporte",
      layers: [
        { label: "Quelle", nodes: [{ label: "Alle Flows", sub: "Zahlungen" }, { label: "Karten", sub: "Buchungen" }, { label: "FX", sub: "Umtausch" }] },
        { label: "Ledger Core", nodes: [{ label: "Double-Entry", sub: "immutable" }, { label: "Konten", sub: "Kontenrahmen" }] },
        { label: "Ausgabe", nodes: [{ label: "Reports", sub: "Echtzeit" }, { label: "DATEV/ERP", sub: "Export" }] },
      ],
    },
    api: {
      method: "GET",
      endpoint: "/v1/ledger/entries",
      code: [
        "// Buchungen für einen Zeitraum exportieren",
        "const entries = await cashfux.ledger.list({",
        '  account: "acc_9f2",',
        '  from: "2025-01-01",',
        '  to: "2025-01-31",',
        '  format: "datev",',
        "});",
        "",
        "entries.download_url // fertiger Export",
      ],
      highlights: [
        "Unveränderliche, auditierbare Buchungen",
        "Export als DATEV, CSV oder JSON",
        "Reports in Echtzeit abrufbar",
      ],
    },
    useCases: [
      { title: "Monatsabschluss", desc: "Abstimmung und Export ohne manuelle Sammelbuchungen." },
      { title: "ERP-Integration", desc: "Ledger-Feed direkt in Ihr Buchhaltungssystem." },
      { title: "Audit & Prüfung", desc: "Lückenloser, unveränderlicher Nachweis für Prüfer." },
    ],
    faq: [
      { q: "Ist das Ledger revisionssicher?", a: "Ja, Buchungen sind doppelt erfasst und unveränderlich – ideal für Audits." },
      { q: "Welche Exportformate gibt es?", a: "DATEV, CSV und JSON über API oder Dashboard." },
      { q: "Lässt sich mein ERP anbinden?", a: "Ja, über API-Feed und Webhooks für laufende Synchronisation." },
      { q: "Wie funktioniert die Abstimmung?", a: "Ledger, Bankstatus und Belege werden automatisch gegeneinander abgeglichen." },
    ],
    related: ["rechnungen", "geschaeftskonten", "apis"],
  },
];
